<template>
    <div class="mechanic-assets-page">
        <div class="mechanic-assets-toolbar">
            <b-form-input
                    v-model.trim="filters.q"
                    type="search"
                    size="sm"
                    placeholder="Поиск"
                    @input="onSearchInput"
            ></b-form-input>

            <v-select
                    v-model="filters.catalog_id"
                    :options="catalogFilterOptions"
                    :reduce="option => option.value"
                    label="text"
                    placeholder="Все группы техники"
                    class="mechanic-assets-select mechanic-assets-catalog-filter"
                    @input="applyFilters"
            ></v-select>

            <v-select
                    v-model="filters.warehouse_id"
                    :options="warehouseFilterOptions"
                    :reduce="option => option.value"
                    label="text"
                    placeholder="Все склады"
                    class="mechanic-assets-select"
                    @input="applyFilters"
            ></v-select>

            <v-select
                    v-model="objectLocationFilter"
                    :options="objectLocationFilterOptions"
                    :filter="filterObjectLocations"
                    :reduce="option => option.value"
                    label="text"
                    placeholder="Все объекты и группы"
                    class="mechanic-assets-select"
                    @input="applyFilters"
            >
                <template #option="option">
                    <span :class="{'pl-3': option.groupId && option.type !== 'group'}">
                        <i :class="option.type === 'group' ? 'fas fa-layer-group' : (option.type === 'direct' ? 'far fa-circle' : 'fas fa-building')" class="mr-1" aria-hidden="true"></i>
                        {{ option.type === 'direct' ? 'На группе без конкретного объекта' : option.text }}
                    </span>
                </template>
            </v-select>

            <v-select
                    v-model="filters.responsible_user_id"
                    :options="userFilterOptions"
                    :reduce="option => option.value"
                    label="text"
                    placeholder="Все МОЛ"
                    class="mechanic-assets-select"
                    @input="applyFilters"
            ></v-select>

            <b-form-checkbox
                    v-model="filters.archive"
                    switch
                    class="mechanic-assets-archive"
                    @change="onArchiveChange"
            >
                Архив
            </b-form-checkbox>
        </div>

        <div class="mechanic-assets-status-panel" @wheel="scrollStatusPanelOnWheel">
            <button
                    v-for="option in statusChipOptions"
                    :key="option.value === null ? 'all' : option.value"
                    type="button"
                    class="mechanic-assets-status-chip"
                    :class="[
                        `mechanic-assets-status-chip--${option.variant}`,
                        { 'mechanic-assets-status-chip--active': statusChipActive(option.value) }
                    ]"
                    @click="setStatusFilter(option.value)"
            >
                <i
                        v-if="statusChipActive(option.value)"
                        class="fas fa-check mechanic-assets-status-chip__check"
                        aria-hidden="true"
                ></i>
                {{ option.text }}
            </button>
        </div>

        <div v-if="activeFilterItems.length" class="mechanic-assets-active-filters">
            <span class="mechanic-assets-active-filters__label">
                <i class="fas fa-filter"></i>
                Включены фильтры:
            </span>
            <button
                    v-for="item in activeFilterItems"
                    :key="item.key"
                    type="button"
                    class="mechanic-assets-active-filter"
                    :class="`mechanic-assets-active-filter--${item.variant}`"
                    :title="`Снять фильтр: ${item.text}`"
                    @click="clearFilterItem(item.key)"
            >
                <i class="fas fa-check" aria-hidden="true"></i>
                <span class="mechanic-assets-active-filter__text">{{ item.text }}</span>
                <i class="fas fa-times mechanic-assets-active-filter__remove" aria-hidden="true"></i>
            </button>
            <button type="button" class="mechanic-assets-active-filters__reset" @click="resetFilters">
                Сбросить все
            </button>
        </div>

        <div class="mechanic-assets-meta">
            <div class="mechanic-assets-view-controls">
                <span class="text-muted">Найдено: {{ total }}</span>
                <b-button-group id="mechanic-assets-view-switch" size="sm" aria-label="Вид справочника">
                    <b-button :variant="viewMode === 'list' ? 'primary' : 'outline-primary'"
                              :aria-pressed="viewMode === 'list' ? 'true' : 'false'"
                              @click="setViewMode('list')">
                        <i class="fas fa-list mr-1" aria-hidden="true"></i>Список
                    </b-button>
                    <b-button :variant="viewMode === 'tiles' ? 'primary' : 'outline-primary'"
                              :aria-pressed="viewMode === 'tiles' ? 'true' : 'false'"
                              @click="setViewMode('tiles')">
                        <i class="fas fa-th-large mr-1" aria-hidden="true"></i>Плитка
                    </b-button>
                </b-button-group>
                <b-popover v-if="viewHintReady && viewPreferencesKey && !viewHintDismissed"
                           target="mechanic-assets-view-switch" :show.sync="viewHintVisible"
                           triggers="manual" placement="bottom" container="body" boundary="viewport"
                           custom-class="mechanic-assets-view-hint">
                    <div class="mechanic-assets-view-hint__content">
                        <span>Справочник можно смотреть списком или плиткой. Выберите удобный вид.</span>
                        <button type="button" class="close" aria-label="Закрыть подсказку" @click="dismissViewHint">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </b-popover>
            </div>
            <div class="mechanic-assets-actions">
                <b-button
                        v-if="canManageAccess"
                        size="sm"
                        variant="outline-primary"
                        @click="openAccessModal"
                >
                    <i class="fas fa-user-shield mr-1"></i>
                    Доступ
                </b-button>
                <b-button
                        v-if="canCreate"
                        size="sm"
                        variant="success"
                        @click="openCreateModal"
                >
                    <i class="fas fa-plus mr-1"></i>
                    Добавить позицию
                </b-button>
                <b-button
                        v-if="canWrite"
                        size="sm"
                        variant="outline-secondary"
                        @click="openWarehousesModal"
                >
                    <i class="fas fa-warehouse mr-1"></i>
                    Склады
                </b-button>
                <b-button
                        v-if="canWrite"
                        size="sm"
                        variant="outline-secondary"
                        @click="openCatalogModal"
                >
                    <i class="fas fa-layer-group mr-1"></i>
                    Группы техники
                </b-button>
            </div>
        </div>

        <b-alert
                v-if="errorMessage"
                variant="danger"
                show
                dismissible
                @dismissed="errorMessage = null"
                class="mb-2"
        >
            {{ errorMessage }}
        </b-alert>

        <div v-if="viewMode === 'tiles' || isMobileLayout" class="mechanic-assets-mobile-sort">
            <b-form-select
                    v-model="tableSortBy"
                    :options="sortOptions"
                    size="sm"
                    aria-label="Сортировка позиций"
                    @change="onMobileSortChange"
            ></b-form-select>
            <b-button
                    size="sm"
                    variant="outline-secondary"
                    @click="toggleMobileSortDirection"
            >
                <i class="fas" :class="tableSortDesc ? 'fa-sort-amount-down' : 'fa-sort-amount-up'"></i>
                {{ tableSortDesc ? 'Убыв.' : 'Возр.' }}
            </b-button>
        </div>

        <b-table
                v-if="viewMode === 'list' && !isMobileLayout"
                class="mechanic-assets-table"
                :items="units"
                :fields="fields"
                :busy="loading"
                responsive="lg"
                striped
                small
                hover
                sticky-header="75vh"
                :sort-by.sync="tableSortBy"
                :sort-desc.sync="tableSortDesc"
                :tbody-tr-class="tableRowClass"
                no-local-sorting
                show-empty
                empty-text="Нет позиций"
                @sort-changed="onSortChanged"
                @row-clicked="openUnitFromRow"
        >
            <template #table-busy>
                <div class="text-center text-muted py-4">
                    Загрузка
                </div>
            </template>

            <template #cell(id)="data">
                <b-button
                        size="sm"
                        variant="link"
                        class="p-0"
                        :data-mechanic-asset-id="data.item.id"
                        @click.stop="openUnit(data.item.id)"
                >
                    #{{ data.item.id }}
                </b-button>
            </template>

            <template #cell(photo)="data">
                <button
                        v-if="data.item.primary_photo"
                        type="button"
                        class="mechanic-assets-photo-thumb"
                        title="Открыть карточку"
                        @click.stop="openUnit(data.item.id)"
                >
                    <img
                            :src="previewFileUrl(data.item, data.item.primary_photo, 96, 72)"
                            :alt="unitTitle(data.item)"
                            loading="lazy"
                            decoding="async"
                    />
                </button>
                <span
                        v-else
                        class="mechanic-assets-photo-thumb mechanic-assets-photo-thumb--empty"
                        title="Фото не выбрано"
                >
                    <i class="fas fa-camera"></i>
                </span>
            </template>

            <template #cell(name)="data">
                <div class="mechanic-assets-name">
                    <a href="" @click.prevent.stop="openUnit(data.item.id)">
                        {{ unitTitle(data.item) }}
                    </a>
                </div>
                <div class="text-muted small">
                    {{ unitSubtitle(data.item) }}
                </div>
            </template>

            <template #cell(status)="data">
                <b-badge :variant="statusVariant(data.item.status)">
                    {{ data.item.status_label || data.item.status }}
                </b-badge>
            </template>

            <template #cell(location)="data">
                <div>{{ data.item.location_type_label || data.item.location_type || '-' }}</div>
                <small class="text-muted">{{ locationName(data.item) }}</small>
                <div v-if="activeTransfer(data.item)" class="mechanic-assets-transfer-card">
                    <div class="mechanic-assets-transfer-title">
                        <b-badge variant="info" class="mechanic-assets-transfer-badge">Перемещается</b-badge>
                        <router-link
                                v-if="transferRequestId(data.item)"
                                :to="transferRequestTo(data.item)"
                                class="mechanic-assets-transfer-request"
                        >
                            Основание: #{{ transferRequestNumber(data.item) }}
                        </router-link>
                    </div>
                    <div class="mechanic-assets-transfer-route">
                        <div class="mechanic-assets-transfer-point">
                            <span>Откуда</span>
                            <strong :title="transferFromText(data.item)">{{ transferFromText(data.item) }}</strong>
                        </div>
                        <div class="mechanic-assets-transfer-point">
                            <span>Куда</span>
                            <strong :title="transferToText(data.item)">{{ transferToText(data.item) }}</strong>
                        </div>
                    </div>
                </div>
            </template>

            <template #cell(responsible)="data">
                <EmployeeLink
                        :user-id="data.item.responsible_user_id"
                        :fallback="data.item.responsible_name || '-'"
                />
            </template>

            <template #cell(quantity)="data">
                <span v-if="isQuantitativeUnit(data.item)">
                    {{ quantityText(data.item) }}
                </span>
                <span v-else class="text-muted">-</span>
            </template>

            <template #cell(meter)="data">
                <span v-if="!isQuantitativeUnit(data.item) && data.item.current_meter_value !== null && data.item.current_meter_value !== undefined">
                    {{ data.item.current_meter_value }} {{ meterTypeLabel(data.item.current_meter_type) }}
                </span>
                <span v-else class="text-muted">-</span>
            </template>

            <template #cell(rental)="data">
                <span v-if="data.item.active_rental_renter_name">
                    {{ data.item.active_rental_renter_name }}
                </span>
                <span v-else class="text-muted">-</span>
            </template>

            <template #cell(actions)="data">
                <b-button
                        size="sm"
                        variant="outline-secondary"
                        title="Открыть"
                        @click.stop="openUnit(data.item.id)"
                >
                    <i class="fas fa-external-link-alt"></i>
                </b-button>
                <b-button
                        v-if="canCreate"
                        size="sm"
                        variant="outline-primary"
                        title="Копировать позицию"
                        class="ml-1"
                        @click.stop="openCopyModal(data.item)"
                >
                    <i class="fas fa-copy"></i>
                </b-button>
            </template>
        </b-table>

        <div v-if="viewMode === 'tiles' || isMobileLayout"
             :class="viewMode === 'tiles' ? 'mechanic-assets-tiles' : 'mechanic-assets-mobile-list'">
            <div v-if="loading" class="text-center text-muted py-4">
                <b-spinner small class="mr-2"></b-spinner>
                Загрузка
            </div>
            <div v-else-if="!units.length" class="text-center text-muted py-4">
                Нет позиций
            </div>
            <template v-else>
                <template v-if="viewMode === 'tiles'">
                    <mechanic-asset-tile
                            v-for="unit in units"
                            :key="unit.id"
                            class="mechanic-assets-mobile-card"
                            :class="{ 'mechanic-assets-highlight-target': isHighlightedUnit(unit) }"
                            :unit="unit"
                            :title="unitTitle(unit)"
                            :location="locationName(unit)"
                            :quantity="isQuantitativeUnit(unit) ? quantityText(unit) : ''"
                            :status-variant="statusVariant(unit.status)"
                            :can-copy="canCreate"
                            :file-url="previewFileUrl"
                            @open="openUnit"
                            @copy="openCopyModal"
                    />
                </template>
                <template v-else>
                <b-card
                        v-for="unit in units"
                        :key="unit.id"
                        no-body
                        class="mechanic-assets-mobile-card"
                        :data-mechanic-asset-id="unit.id"
                        :class="{ 'mechanic-assets-highlight-target': isHighlightedUnit(unit) }"
                >
                    <span
                            class="mechanic-assets-mobile-card__accent"
                            :class="`bg-${statusVariant(unit.status)}`"
                            aria-hidden="true"
                    ></span>
                    <b-button
                            v-if="canCreate"
                            size="sm"
                            variant="outline-secondary"
                            class="mechanic-assets-mobile-card__copy"
                            title="Копировать позицию"
                            aria-label="Копировать позицию"
                            @click.stop="openCopyModal(unit)"
                    >
                        <i class="fas fa-copy"></i>
                    </b-button>
                    <div class="mechanic-assets-mobile-card__header">
                        <button
                                v-if="hasCardPhoto(unit)"
                                type="button"
                                class="mechanic-assets-mobile-card__photo"
                                title="Открыть карточку"
                                @click.stop="openUnit(unit.id)"
                        >
                            <img
                                    :src="cardPhotoUrl(unit)"
                                    :alt="unitTitle(unit)"
                                    loading="lazy"
                                    decoding="async"
                                    @error="markCardPhotoFailed(unit)"
                            />
                        </button>
                        <button
                                v-else
                                type="button"
                                class="mechanic-assets-mobile-card__photo mechanic-assets-mobile-card__photo--empty"
                                title="Нет фото. Открыть карточку"
                                :aria-label="`Нет фото. Открыть карточку: ${unitTitle(unit)}`"
                                @click="openUnit(unit.id)"
                        >
                            <i class="fas fa-camera" aria-hidden="true"></i>
                        </button>
                        <div class="mechanic-assets-mobile-card__title-block">
                            <a href="" class="mechanic-assets-mobile-card__title" @click.prevent="openUnit(unit.id)">
                                {{ unitTitle(unit) }}
                            </a>
                            <div class="text-muted small">
                                #{{ unit.id }}
                                <span v-if="unit.catalog_name"> · {{ unit.catalog_name }}</span>
                            </div>
                            <div v-if="unitSubtitle(unit)" class="text-muted small">
                                {{ unitSubtitle(unit) }}
                            </div>
                        </div>
                        <b-badge :variant="statusVariant(unit.status)" class="mechanic-assets-mobile-card__status">
                            {{ unit.status_label || unit.status }}
                        </b-badge>
                    </div>

                    <div class="mechanic-assets-mobile-card__body">
                        <div>
                            <span class="text-muted">Место</span>
                            <strong>{{ unit.location_type_label || unit.location_type || '-' }}</strong>
                            <small class="d-block text-muted">{{ locationName(unit) || '-' }}</small>
                            <div v-if="activeTransfer(unit)" class="mechanic-assets-transfer-card mechanic-assets-transfer-card--mobile">
                                <div class="mechanic-assets-transfer-title">
                                    <b-badge variant="info" class="mechanic-assets-transfer-badge">Перемещается</b-badge>
                                    <router-link
                                            v-if="transferRequestId(unit)"
                                            :to="transferRequestTo(unit)"
                                            class="mechanic-assets-transfer-request"
                                    >
                                        Основание: #{{ transferRequestNumber(unit) }}
                                    </router-link>
                                </div>
                                <div class="mechanic-assets-transfer-route">
                                    <div class="mechanic-assets-transfer-point">
                                        <span>Откуда</span>
                                        <strong :title="transferFromText(unit)">{{ transferFromText(unit) }}</strong>
                                    </div>
                                    <div class="mechanic-assets-transfer-point">
                                        <span>Куда</span>
                                        <strong :title="transferToText(unit)">{{ transferToText(unit) }}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <span class="text-muted">МОЛ</span>
                            <strong>
                                <EmployeeLink
                                        :user-id="unit.responsible_user_id"
                                        :fallback="unit.responsible_name || '-'"
                                />
                            </strong>
                        </div>
                        <div>
                            <span class="text-muted">{{ isQuantitativeUnit(unit) ? 'Остаток' : 'Счетчик' }}</span>
                            <strong>{{ mobileMeterOrQuantityText(unit) }}</strong>
                        </div>
                        <div v-if="unit.active_rental_renter_name">
                            <span class="text-muted">Аренда</span>
                            <strong>{{ unit.active_rental_renter_name }}</strong>
                        </div>
                    </div>

                    <div class="mechanic-assets-mobile-card__footer">
                        <b-button
                                size="sm"
                                variant="outline-primary"
                                @click="openUnit(unit.id)"
                        >
                            <i class="fas fa-eye mr-1"></i>
                            Подробнее
                        </b-button>
                    </div>
                </b-card>
                </template>
            </template>
        </div>

        <div v-if="total > 0" class="mechanic-assets-bottom-pagination">
            <div class="mechanic-assets-bottom-pagination__summary text-muted">
                Найдено: {{ total }}
            </div>
            <div class="mechanic-assets-pagination">
                <b-pagination
                        v-model="currentPage"
                        :total-rows="total"
                        :per-page="limit"
                        size="sm"
                        :limit="paginationLimit"
                        :first-number="!isMobileLayout"
                        :last-number="!isMobileLayout"
                        class="mb-0"
                        @input="onPageChange"
                ></b-pagination>
                <b-form-select
                        v-model="limit"
                        :options="pageOptions"
                        size="sm"
                        class="mechanic-assets-page-size"
                        @change="onLimitChange"
                ></b-form-select>
            </div>
        </div>

        <b-modal
                v-model="showCreateModal"
                size="lg"
                :title="createModalTitle"
                :ok-title="createModalOkTitle"
                cancel-title="Отмена"
                :busy="isSubmitting"
                :ok-disabled="!isCreateFormValid"
                @ok.prevent="submitCreateForm"
                @hidden="resetCreateForm"
        >
            <b-form @submit.stop.prevent="submitCreateForm">
                <mechanic-asset-position-form-fields
                        v-model="createForm"
                        :accounting-type.sync="createAccountingType"
                        :accounting-type-options="createAccountingTypeOptions"
                        :status-options="createStatusOptions"
                        :location-type-options="locationTypeOptions"
                        :warehouse-options="createWarehouseOptions"
                        :object-options="createObjectOptions"
                        :object-group-options="assignableObjectGroupOptions"
                        :user-options="createUserOptions"
                        :meter-type-options="meterTypeOptions"
                        :accounting-company-options="accountingCompanyOptions"
                        :quantity-min="0"
                        :disabled="isSubmitting"
                >
                    <template #catalog>
                        <b-form-group label="Группа техники / тип">
                            <div class="mechanic-assets-catalog-select-row">
                                <v-select
                                        v-model="createForm.catalog_id"
                                        :options="catalogOptions"
                                        :reduce="option => option.value"
                                        label="text"
                                        placeholder="Выберите группу техники"
                                        class="mechanic-assets-select flex-grow-1"
                                ></v-select>
                                <b-button
                                        v-if="canCreate"
                                        type="button"
                                        size="sm"
                                        variant="outline-success"
                                        :disabled="inlineCatalogSubmitting"
                                        @click.prevent="openInlineCatalogForm"
                                >
                                    <i class="fas fa-plus mr-1"></i>
                                    Новая группа
                                </b-button>
                            </div>
                            <div
                                    v-if="showInlineCatalogForm"
                                    class="mechanic-assets-inline-catalog-form"
                                    @keydown.enter.prevent="submitInlineCatalogForm"
                            >
                                <b-form-input
                                        v-model.trim="inlineCatalogForm.name"
                                        size="sm"
                                        placeholder="Название группы"
                                ></b-form-input>
                                <v-select
                                        v-model="inlineCatalogForm.category"
                                        :options="catalogCategoryOptions"
                                        :reduce="option => option.value"
                                        :create-option="createCatalogCategoryOption"
                                        label="text"
                                        placeholder="Категория"
                                        taggable
                                        class="mechanic-assets-select"
                                ></v-select>
                                <b-form-input v-model.trim="inlineCatalogForm.brand" size="sm" placeholder="Марка"></b-form-input>
                                <b-form-input v-model.trim="inlineCatalogForm.model" size="sm" placeholder="Модель"></b-form-input>
                                <b-button
                                        type="button"
                                        size="sm"
                                        variant="success"
                                        :disabled="inlineCatalogSubmitting || !inlineCatalogForm.name"
                                        @click.prevent="submitInlineCatalogForm"
                                >
                                    <b-spinner v-if="inlineCatalogSubmitting" small class="mr-1"></b-spinner>
                                    Создать группу
                                </b-button>
                                <b-button
                                        type="button"
                                        size="sm"
                                        variant="outline-secondary"
                                        :disabled="inlineCatalogSubmitting"
                                        @click.prevent="resetInlineCatalogForm"
                                >
                                    Отмена
                                </b-button>
                            </div>
                        </b-form-group>
                    </template>
                </mechanic-asset-position-form-fields>
            </b-form>
        </b-modal>

        <b-modal
                v-model="showWarehousesModal"
                size="lg"
                title="Склады и ремзоны"
                ok-only
                ok-title="Закрыть"
                @hidden="resetWarehouseForm"
        >
            <b-form class="mechanic-assets-management-form" @submit.stop.prevent="submitWarehouseForm">
                <b-form-input
                        v-model.trim="warehouseForm.name"
                        size="sm"
                        placeholder="Название"
                ></b-form-input>
                <b-form-select
                        v-model="warehouseForm.warehouse_type"
                        :options="warehouseTypeOptions"
                        size="sm"
                ></b-form-select>
                <b-form-checkbox v-model="warehouseForm.is_active" switch class="mb-0">
                    Активен
                </b-form-checkbox>
                <b-button
                        type="submit"
                        size="sm"
                        variant="success"
                        :disabled="isSubmitting || !warehouseForm.name"
                >
                    {{ warehouseForm.id ? 'Сохранить' : 'Добавить' }}
                </b-button>
                <b-button
                        v-if="warehouseForm.id"
                        size="sm"
                        variant="outline-secondary"
                        @click="resetWarehouseForm"
                >
                    Отмена
                </b-button>
            </b-form>
            <b-table
                    :items="state.warehouses"
                    :fields="warehouseManageFields"
                    small
                    responsive="sm"
                    show-empty
                    empty-text="Склады не заведены"
            >
                <template #cell(warehouse_type)="data">
                    {{ warehouseTypeText(data.item.warehouse_type) }}
                </template>
                <template #cell(actions)="data">
                    <b-button size="sm" variant="outline-success" class="mr-1" @click="editWarehouse(data.item)">
                        <i class="fas fa-pen"></i>
                    </b-button>
                    <b-button size="sm" variant="outline-danger" @click="archiveWarehouse(data.item)">
                        <i class="fas fa-times"></i>
                    </b-button>
                </template>
            </b-table>
        </b-modal>

        <b-modal
                v-model="showCatalogModal"
                size="xl"
                title="Группы техники"
                ok-only
                ok-title="Закрыть"
                @hidden="resetCatalogForm"
        >
            <b-form class="mechanic-assets-catalog-form" @submit.stop.prevent="submitCatalogForm">
                <b-form-input
                        v-model.trim="catalogForm.name"
                        size="sm"
                        placeholder="Название"
                ></b-form-input>
                <v-select
                        v-model="catalogForm.category"
                        :options="catalogCategoryOptions"
                        :reduce="option => option.value"
                        :create-option="createCatalogCategoryOption"
                        label="text"
                        placeholder="Категория"
                        taggable
                        class="mechanic-assets-select"
                ></v-select>
                <b-form-input v-model.trim="catalogForm.brand" size="sm" placeholder="Марка"></b-form-input>
                <b-form-input v-model.trim="catalogForm.model" size="sm" placeholder="Модель"></b-form-input>
                <b-form-checkbox v-model="catalogForm.is_active" switch class="mb-0">
                    Активна
                </b-form-checkbox>
                <b-button
                        type="submit"
                        size="sm"
                        variant="success"
                        :disabled="isSubmitting || !catalogForm.name"
                >
                    {{ catalogForm.id ? 'Сохранить' : 'Добавить' }}
                </b-button>
                <b-button
                        v-if="catalogForm.id"
                        size="sm"
                        variant="outline-secondary"
                        @click="resetCatalogForm"
                >
                    Отмена
                </b-button>
            </b-form>
            <b-table
                    :items="state.catalog"
                    :fields="catalogManageFields"
                    small
                    responsive="md"
                    show-empty
                    empty-text="Группы техники не заведены"
            >
                <template #cell(category)="data">
                    {{ catalogCategoryText(data.item.category) }}
                </template>
                <template #cell(actions)="data">
                    <b-button size="sm" variant="outline-success" class="mr-1" @click="editCatalog(data.item)">
                        <i class="fas fa-pen"></i>
                    </b-button>
                    <b-button size="sm" variant="outline-danger" @click="archiveCatalog(data.item)">
                        <i class="fas fa-times"></i>
                    </b-button>
                </template>
            </b-table>
        </b-modal>

        <b-modal
                v-model="showAccessModal"
                size="lg"
                title="Настройка доступа к справочнику механиков"
                ok-title="Сохранить"
                cancel-title="Отмена"
                :busy="isSettingsSubmitting"
                :ok-disabled="isSettingsSubmitting"
                @ok.prevent="saveAccessSettings"
                @hide="resetAccessModal"
        >
            <b-alert show variant="info" class="mb-3">
                Роли на объекте дают доступ только к позициям, которые сейчас находятся на этом объекте.
                Позиции на складе, в ремзоне, аренде или без объекта по таким правилам не видны.
            </b-alert>

            <b-form-checkbox v-model="settingsForm.enabled" switch class="mb-3">
                Использовать настройку доступа
            </b-form-checkbox>

            <div v-if="settingsForm.enabled">
                <b-form-checkbox v-model="settingsForm.read_history_for_readers" switch class="mb-3">
                    Разрешить историю пользователям только с правом чтения
                    <small class="text-muted d-block">
                        Если выключено, историю изменений видят только админы и пользователи с правом редактирования.
                        Это скрывает прежние объекты техники от пользователей, которым доступен только текущий объект.
                    </small>
                </b-form-checkbox>

                <div class="border rounded p-3 mb-3">
                    <h6 class="mb-2">
                        <i class="fas fa-eye"></i>
                        Кто видит справочник
                    </h6>
                    <MultipleParticipantsConfig
                            v-model="settingsForm.read"
                            :users="accessUserOptions"
                            :departments="accessDepartmentOptions"
                            participant-label="правило чтения"
                            :show-object-roles="true"
                            :show-fallback-option="false"
                            type-label="Тип правила"
                            all-text="Правило распространяется на всех пользователей системы."
                            hint-text="Правила работают по ИЛИ: достаточно совпадения с одним правилом."
                            empty-text="Правила чтения не добавлены."
                    />
                </div>

                <div class="border rounded p-3">
                    <h6 class="mb-2">
                        <i class="fas fa-pen"></i>
                        Кто редактирует справочник
                    </h6>
                    <MultipleParticipantsConfig
                            v-model="settingsForm.write"
                            :users="accessUserOptions"
                            :departments="accessDepartmentOptions"
                            participant-label="правило редактирования"
                            :show-object-roles="true"
                            :show-fallback-option="false"
                            type-label="Тип правила"
                            all-text="Правило распространяется на всех пользователей системы."
                            hint-text="Право редактирования также дает чтение доступных позиций."
                            empty-text="Правила редактирования не добавлены."
                    />
                </div>
            </div>

            <b-alert v-else show variant="secondary" class="mb-0">
                Пока настройка выключена, работают прежние права системы.
            </b-alert>
        </b-modal>
    </div>
</template>

<script>
import EmployeeLink from "@/components/EmployeeLink.vue";
import MultipleParticipantsConfig from "@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue";
import MechanicAssetPositionFormFields from "@/views/MechanicAssets/components/MechanicAssetPositionFormFields.vue";
import MechanicAssetTile from "@/views/MechanicAssets/components/MechanicAssetTile.vue";
import {
    MECHANIC_ASSET_ACCOUNTING_TYPE_LABELS as ACCOUNTING_TYPE_LABELS,
    MECHANIC_ASSET_CATALOG_CATEGORY_LABELS as CATALOG_CATEGORY_LABELS,
    MECHANIC_ASSET_WAREHOUSE_TYPE_LABELS as WAREHOUSE_TYPE_LABELS,
    getMechanicAssetStatusVariant
} from '@/utils/mechanicAssets';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

const SESSION_KEY = `mechanicAssetsListReturn`;
const SESSION_TTL_MS = 60 * 60 * 1000;
const HIGHLIGHT_CLEANUP_DELAY_MS = 2800;
const HIGHLIGHT_BLINK_DURATION_MS = 1900;

function emptyWarehouseForm() {
    return {
        id: null,
        name: null,
        warehouse_type: `warehouse`,
        is_active: true,
        comment: null
    };
}

function emptyCatalogForm() {
    return {
        id: null,
        name: null,
        category: `machinery`,
        brand: null,
        model: null,
        is_active: true,
        comment: null
    };
}

function emptyCreateForm() {
    return {
        catalog_id: null,
        display_name: null,
        status: `stock`,
        location_type: `warehouse`,
        warehouse_id: null,
        building_process_id: null,
        object_group_id: null,
        responsible_user_id: null,
        quantity: 1,
        accounting_type: `inventory`,
        unit: null,
        inventory_number: null,
        serial_number: null,
        plate_number: null,
        current_meter_type: `moto_hour`,
        current_meter_value: null,
        technical_condition: null,
        accounting_company_name: null,
        owner_name: null,
        owner_kind: `own`,
        comment: null
    };
}

function defaultFilters() {
    return {
        q: ``,
        status: null,
        catalog_id: null,
        location_type: null,
        warehouse_id: null,
        building_process_id: null,
        object_group_id: null,
        object_group_scope_id: null,
        responsible_user_id: null,
        archive: false
    };
}

function createDefaultAccessConfig() {
    return {
        enabled: false,
        read_history_for_readers: false,
        read: {participants: []},
        write: {participants: []}
    };
}

function cloneJson(value) {
    return JSON.parse(JSON.stringify(value || {}));
}

export default {
    name: `MechanicAssets`,
    components: {
        EmployeeLink,
        MultipleParticipantsConfig,
        MechanicAssetPositionFormFields,
        MechanicAssetTile
    },
    data() {
        return {
            filters: defaultFilters(),
            limit: 50,
            currentPage: 1,
            tableSortBy: `id`,
            tableSortDesc: true,
            isMobileLayout: false,
            viewMode: `list`,
            viewHintReady: false,
            viewHintVisible: false,
            viewHintDismissed: false,
            failedCardPhotos: {},
            pageOptions: [25, 50, 100, 200],
            sortOptions: [
                {value: `id`, text: `ID`},
                {value: `name`, text: `Позиция`},
                {value: `status`, text: `Статус`},
                {value: `location`, text: `Место`},
                {value: `responsible`, text: `МОЛ`},
                {value: `quantity`, text: `Остаток`},
                {value: `meter`, text: `Счетчик`},
                {value: `rental`, text: `Аренда`}
            ],
            errorMessage: null,
            searchTimer: null,
            autoRefreshInProgress: false,
            initialDataLoading: false,
            highlightCleanupTimer: null,
            highlightedUnitId: null,
            showCreateModal: false,
            showWarehousesModal: false,
            showCatalogModal: false,
            showAccessModal: false,
            isSubmitting: false,
            isSettingsSubmitting: false,
            createModalMode: `create`,
            createAccountingType: `inventory`,
            inlineCatalogSubmitting: false,
            createForm: emptyCreateForm(),
            settingsForm: createDefaultAccessConfig(),
            inlineCatalogForm: emptyCatalogForm(),
            showInlineCatalogForm: false,
            warehouseForm: emptyWarehouseForm(),
            catalogForm: emptyCatalogForm(),
            warehouseTypeOptions: [
                {value: `warehouse`, text: `Склад`},
                {value: `repair`, text: `Ремзона`},
                {value: `storage`, text: `Хранение`}
            ],
            createAccountingTypeOptions: [
                {value: `inventory`, text: `Инвентарная`},
                {value: `quantity`, text: `Количественная`}
            ],
            meterTypeOptions: [
                {value: null, text: `Не указан`},
                {value: `km`, text: `Километры`},
                {value: `moto_hour`, text: `Моточасы`}
            ],
            fields: [
                {key: `id`, label: `ID`, class: `text-nowrap`, sortable: true},
                {key: `photo`, label: ``, class: `mechanic-assets-photo-col text-center`},
                {key: `name`, label: `Позиция`, sortable: true},
                {key: `status`, label: `Статус`, class: `text-nowrap`, sortable: true},
                {key: `location`, label: `Место`, sortable: true},
                {key: `responsible`, label: `МОЛ`, sortable: true},
                {key: `quantity`, label: `Остаток`, class: `text-nowrap`, sortable: true},
                {key: `meter`, label: `Счетчик`, class: `text-nowrap`, sortable: true},
                {key: `rental`, label: `Аренда`, sortable: true},
                {key: `actions`, label: ``, class: `text-nowrap text-right`}
            ],
            warehouseManageFields: [
                {key: `name`, label: `Название`},
                {key: `warehouse_type`, label: `Тип`},
                {key: `actions`, label: ``, class: `text-nowrap text-right`}
            ],
            catalogManageFields: [
                {key: `name`, label: `Название`},
                {key: `category`, label: `Категория`},
                {key: `brand`, label: `Марка`},
                {key: `model`, label: `Модель`},
                {key: `actions`, label: ``, class: `text-nowrap text-right`}
            ]
        };
    },
    computed: {
        viewPreferencesKey() {
            const user = this.$store.getters.currentUser;
            const userId = user && (user.userId || user.user_id || user.id);
            return userId && user.company ? `mechanicAssetsView:${user.company}:${userId}` : null;
        },
        state() {
            return this.$store.state.mechanicAssets;
        },
        units() {
            return this.state.units;
        },
        total() {
            return this.state.total;
        },
        loading() {
            return this.state.loading;
        },
        createModalTitle() {
            return this.createModalMode === `copy` ? `Копирование позиции` : `Создание позиции`;
        },
        createModalOkTitle() {
            return this.createModalMode === `copy` ? `Создать копию` : `Создать`;
        },
        accessSettings() {
            return this.state.accessSettings || {};
        },
        legacyCanWrite() {
            if (!this.$can) {
                return false;
            }
            return this.$can(`manage`, `mechanicAssets`)
                || this.$can(`write`, `mechanicAssets`)
                || this.$can(`create`, `mechanicAssets`)
                || this.$can(`update`, `mechanicAssets`);
        },
        canCreate() {
            return this.accessSettings.can_create === true || this.legacyCanWrite;
        },
        canManageAccess() {
            return this.accessSettings.can_manage_access === true;
        },
        canWrite() {
            return this.canCreate || this.legacyCanWrite;
        },
        statusOptions() {
            return [{value: null, text: `Все статусы`}].concat(this.state.statuses);
        },
        createStatusOptions() {
            return this.state.statuses;
        },
        accountingCompanyOptions() {
            const options = this.state.positionFieldOptions || {};
            return Array.isArray(options.accounting_company_names) ? options.accounting_company_names : [];
        },
        actualFilterValues() {
            const values = {
                status: {},
                catalog_id: {},
                warehouse_id: {},
                building_process_id: {},
                object_group_id: {},
                responsible_user_id: {}
            };
            const remember = (key, value) => {
                if (value !== null && value !== undefined && value !== ``) {
                    values[key][String(value)] = true;
                }
            };
            const options = this.state.filterOptions || {};
            Object.keys(values).forEach(key => {
                const list = Array.isArray(options[key]) ? options[key] : [];
                list.forEach(value => remember(key, value));
            });

            remember(`status`, this.filters.status);
            remember(`catalog_id`, this.filters.catalog_id);
            remember(`warehouse_id`, this.filters.warehouse_id);
            remember(`building_process_id`, this.filters.building_process_id);
            remember(`object_group_id`, this.filters.object_group_id);
            remember(`responsible_user_id`, this.filters.responsible_user_id);

            return values;
        },
        statusChipOptions() {
            const visibleStatuses = this.actualFilterValues.status;
            const optionsByValue = {};
            (this.state.statuses || []).forEach(option => {
                if (option && option.value) {
                    optionsByValue[option.value] = option;
                }
            });

            const visibleOptions = (this.state.statuses || [])
                .filter(option => option && visibleStatuses[option.value])
                .map(option => ({
                    value: option.value,
                    text: option.text,
                    variant: option.variant || this.statusVariant(option.value)
                }));
            Object.keys(visibleStatuses).forEach(status => {
                if (!optionsByValue[status]) {
                    visibleOptions.push({
                        value: status,
                        text: status,
                        variant: this.statusVariant(status)
                    });
                }
            });

            return [{value: null, text: `Все`, variant: `secondary`}].concat(visibleOptions);
        },
        paginationLimit() {
            return this.isMobileLayout ? 3 : 7;
        },
        locationTypeOptions() {
            return this.state.locationTypes;
        },
        catalogCategoryOptions() {
            const options = Object.keys(CATALOG_CATEGORY_LABELS).map(value => ({
                value,
                text: CATALOG_CATEGORY_LABELS[value]
            }));
            const known = {};
            options.forEach(option => {
                known[String(option.value).toLowerCase()] = true;
            });
            const remember = value => {
                const normalized = this.normalizeCatalogCategory(value);
                if (!normalized) {
                    return;
                }
                const key = normalized.toLowerCase();
                if (known[key]) {
                    return;
                }
                known[key] = true;
                options.push({
                    value: normalized,
                    text: this.catalogCategoryText(normalized)
                });
            };
            (this.state.catalog || []).forEach(row => remember(row.category));
            remember(this.inlineCatalogForm.category);
            remember(this.catalogForm.category);
            return options;
        },
        catalogOptions() {
            return [{value: null, text: `Выберите группу техники`}].concat(this.state.catalog.map(row => ({
                value: row.id,
                text: this.catalogOptionText(row)
            })));
        },
        catalogFilterOptions() {
            const options = [{value: null, text: `Все группы техники`}].concat(this.state.catalog.map(row => ({
                value: row.id,
                text: this.catalogOptionText(row)
            })));
            return this.filterActualOptions(options, this.actualFilterValues.catalog_id, this.filters.catalog_id);
        },
        warehouseOptions() {
            return [{value: null, text: `Все склады`}].concat(this.state.warehouses.map(row => ({
                value: row.id,
                text: row.name
            })));
        },
        warehouseFilterOptions() {
            return this.filterActualOptions(
                this.warehouseOptions,
                this.actualFilterValues.warehouse_id,
                this.filters.warehouse_id
            );
        },
        createWarehouseOptions() {
            return [{value: null, text: `Не указан`}].concat(this.state.warehouses.map(row => ({
                value: row.id,
                text: row.name
            })));
        },
        objectOptions() {
            const processes = this.$store.state.objectsInProcess || [];
            const options = processes.map(process => {
                const name = process.object_name
                    || (process.bo && process.bo.name)
                    || `Процесс ${process.building_process_id}`;
                const contract = process.contract_name ? ` (${process.contract_name})` : ``;
                return {
                    value: process.building_process_id,
                    groupId: process.object_group_id || null,
                    text: `${name}${contract}`
                };
            }).sort((a, b) => a.text.localeCompare(b.text, `ru`));
            return [{value: null, text: `Все объекты`}].concat(options);
        },
        objectGroupOptions() {
            return (this.state.objectGroups || []).map(group => ({value: group.id, text: group.name}));
        },
        assignableObjectGroupOptions() {
            return (this.state.objectGroups || []).filter(group => group.can_assign)
                .map(group => ({value: group.id, text: group.name}));
        },
        objectLocationFilter: {
            get() {
                if (this.filters.object_group_scope_id) return `group:${this.filters.object_group_scope_id}`;
                if (this.filters.object_group_id) return `direct:${this.filters.object_group_id}`;
                if (this.filters.building_process_id) return `object:${this.filters.building_process_id}`;
                return null;
            },
            set(value) {
                const [type, id] = String(value || ``).split(`:`);
                this.filters.object_group_scope_id = type === `group` ? Number(id) : null;
                this.filters.object_group_id = type === `direct` ? Number(id) : null;
                this.filters.building_process_id = type === `object` ? Number(id) : null;
            }
        },
        objectLocationFilterOptions() {
            const objects = this.objectOptions.filter(option => option.value).map(option => ({
                ...option, type: `object`, value: `object:${option.value}`
            }));
            const grouped = new Set();
            const result = [];
            this.objectGroupOptions.forEach(group => {
                result.push({type: `group`, value: `group:${group.value}`, groupId: group.value,
                    text: `${group.text} — вся техника`});
                result.push({type: `direct`, value: `direct:${group.value}`, groupId: group.value,
                    text: `${group.text} — на группе без конкретного объекта`});
                objects.filter(object => Number(object.groupId) === Number(group.value)).forEach(object => {
                    grouped.add(object.value);
                    result.push(object);
                });
            });
            return result.concat(objects.filter(object => !grouped.has(object.value)));
        },
        objectFilterOptions() {
            return this.filterActualOptions(
                this.objectOptions,
                this.actualFilterValues.building_process_id,
                this.filters.building_process_id
            );
        },
        createObjectOptions() {
            return [{value: null, text: `Не указан`}].concat(this.objectOptions.slice(1));
        },
        userOptions() {
            const users = this.$store.state.users || [];
            const options = users
                .filter(user => user.active && !user.isBot)
                .map(user => ({
                    value: user.user_id,
                    text: this.userName(user)
                }))
                .sort((a, b) => a.text.localeCompare(b.text, `ru`));
            return [{value: null, text: `Все МОЛ`}].concat(options);
        },
        userFilterOptions() {
            return this.filterActualOptions(
                this.userOptions,
                this.actualFilterValues.responsible_user_id,
                this.filters.responsible_user_id
            );
        },
        createUserOptions() {
            return [{value: null, text: `Не указан`}].concat(this.userOptions.slice(1));
        },
        accessUserOptions() {
            const users = this.$store.state.users || [];
            return users
                .filter(user => user.active && !user.isBot)
                .map(user => ({
                    value: user.user_id,
                    text: this.userName(user)
                }))
                .sort((a, b) => a.text.localeCompare(b.text, `ru`));
        },
        accessDepartmentOptions() {
            const departments = this.$store.state.departments || [];
            return departments
                .filter(department => department.is_active !== false)
                .map(department => ({
                    value: department.id,
                    text: department.name
                }))
                .sort((a, b) => a.text.localeCompare(b.text, `ru`));
        },
        createUsesWarehouse() {
            return [`warehouse`, `repair`, `external_repair`].indexOf(this.createForm.location_type) !== -1;
        },
        createUsesObject() {
            return this.createForm.location_type === `object`;
        },
        selectedCreateCatalog() {
            const id = Number(this.createForm.catalog_id);
            if (!Number.isInteger(id) || id <= 0) {
                return null;
            }
            const row = (this.state.catalog || []).find(catalog => Number(catalog.id) === id) || null;
            if (!row) {
                return null;
            }
            return row;
        },
        createUsesQuantityAccounting() {
            return this.createAccountingType === `quantity`;
        },
        isCreateFormValid() {
            if (this.createForm.location_type === `object_group` && !this.createForm.object_group_id) return false;
            if (this.isSubmitting || this.inlineCatalogSubmitting || !this.createForm.catalog_id) {
                return false;
            }
            if (this.createUsesQuantityAccounting) {
                return Number(this.createForm.quantity) >= 0 && Boolean(this.createForm.unit);
            }
            return true;
        },
        activeFilterItems() {
            const result = [];
            if (this.filters.q) {
                result.push({key: `q`, label: `Поиск`, text: `Поиск: ${this.filters.q}`, variant: `primary`});
            }
            this.addFilterItem(result, `status`, `Статус`, this.statusOptions, this.filters.status, this.statusVariant(this.filters.status));
            this.addFilterItem(result, `catalog_id`, `Группа техники`, this.catalogFilterOptions, this.filters.catalog_id, `warning`);
            this.addFilterItem(result, `warehouse_id`, `Склад`, this.warehouseFilterOptions, this.filters.warehouse_id, `secondary`);
            this.addFilterItem(result, `building_process_id`, `Объект`, this.objectFilterOptions, this.filters.building_process_id, `info`);
            this.addFilterItem(result, `object_group_scope_id`, `Группа и её объекты`, this.objectGroupOptions, this.filters.object_group_scope_id, `info`);
            this.addFilterItem(result, `object_group_id`, `Без конкретного объекта`, this.objectGroupOptions, this.filters.object_group_id, `info`);
            this.addFilterItem(result, `responsible_user_id`, `МОЛ`, this.userFilterOptions, this.filters.responsible_user_id, `dark`);
            if (this.filters.archive) {
                result.push({key: `archive`, label: `Режим`, text: `Архив`, variant: `secondary`});
            }
            return result;
        }
    },
    watch: {
        viewPreferencesKey: {
            immediate: true,
            handler() {
                this.restoreViewPreferences();
            }
        },
        'createForm.location_type'() {
            this.normalizeCreateLocation();
        },
        'createForm.catalog_id'() {
            this.applyCreateCatalogDefaults();
        },
        createAccountingType() {
            this.normalizeCreateAccountingType();
        }
    },
    async mounted() {
        this.syncListControlsFromState();
        this.updateMobileLayout();
        this.$nextTick(() => {
            if (this._isDestroyed) return;
            this.viewHintReady = true;
            this.viewHintVisible = !this.viewHintDismissed;
        });
        await this.fetchInitialData();
        if (this._isDestroyed) {
            return;
        }
        window.addEventListener(`resize`, this.updateMobileLayout);
        window.addEventListener(`focus`, this.autoRefreshOnResume);
        window.addEventListener(`pageshow`, this.handlePageShow);
        document.addEventListener(`visibilitychange`, this.handleVisibilityChange);
    },
    beforeDestroy() {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.clearHighlightCleanupTimer();
        window.removeEventListener(`resize`, this.updateMobileLayout);
        window.removeEventListener(`focus`, this.autoRefreshOnResume);
        window.removeEventListener(`pageshow`, this.handlePageShow);
        document.removeEventListener(`visibilitychange`, this.handleVisibilityChange);
    },
    methods: {
        restoreViewPreferences() {
            this.viewMode = `list`;
            this.viewHintDismissed = false;
            try {
                const preferences = this.viewPreferencesKey
                    ? JSON.parse(window.localStorage.getItem(this.viewPreferencesKey) || `{}`) : {};
                this.viewMode = preferences && preferences.mode === `tiles` ? `tiles` : `list`;
                this.viewHintDismissed = Boolean(preferences && preferences.hintDismissed === true);
            } catch (error) { /* Local preferences must not block the registry. */ }
            this.viewHintVisible = this.viewHintReady && !this.viewHintDismissed;
        },
        saveViewPreferences() {
            if (!this.viewPreferencesKey) return;
            try {
                window.localStorage.setItem(this.viewPreferencesKey, JSON.stringify({
                    mode: this.viewMode,
                    hintDismissed: this.viewHintDismissed
                }));
            } catch (error) { /* Keep the current choice in component state. */ }
        },
        dismissViewHint() {
            this.viewHintVisible = false;
            this.viewHintDismissed = true;
            this.saveViewPreferences();
        },
        setViewMode(mode) {
            if (mode !== `list` && mode !== `tiles`) return;
            const changed = this.viewMode !== mode;
            this.viewMode = mode;
            this.dismissViewHint();
            if (changed && this.highlightedUnitId) {
                this.scrollToHighlight(this.highlightedUnitId);
            }
        },
        cardPhotoKey(unit) {
            return `${unit.id}:${unit.primary_photo && unit.primary_photo.id}:${this.viewMode}`;
        },
        cardPhotoUrl(unit) {
            return this.previewFileUrl(unit, unit.primary_photo, 96, 72);
        },
        hasCardPhoto(unit) {
            return Boolean(unit.primary_photo && !this.failedCardPhotos[this.cardPhotoKey(unit)]);
        },
        markCardPhotoFailed(unit) {
            this.$set(this.failedCardPhotos, this.cardPhotoKey(unit), true);
        },
        syncListControlsFromState() {
            const state = this.state || {};
            const limit = Number(state.limit) || this.limit || 50;
            const offset = Number(state.offset) || 0;

            this.limit = limit;
            this.currentPage = Math.max(1, Math.floor(offset / limit) + 1);
            this.tableSortBy = state.sortBy || this.tableSortBy;
            this.tableSortDesc = Boolean(state.sortDesc);
            this.filters = Object.assign(defaultFilters(), state.filters || {});
        },
        applyListContext(context) {
            if (!context) {
                return;
            }

            const limit = Number(context.limit) || this.limit || 50;
            const page = Math.max(1, Number(context.page) || 1);
            this.limit = limit;
            this.currentPage = page;
            this.tableSortBy = context.sortBy || this.tableSortBy;
            this.tableSortDesc = Boolean(context.sortDesc);
            this.filters = Object.assign(defaultFilters(), context.filters || {});

            this.$store.commit(`mechanicAssets/setFilters`, this.normalizedFilters());
            this.$store.commit(`mechanicAssets/setSort`, {
                sortBy: this.tableSortBy,
                sortDesc: this.tableSortDesc
            });
            this.$store.commit(`mechanicAssets/setPagination`, {
                limit,
                offset: (page - 1) * limit
            });
        },
        saveReturnSession(unitId) {
            try {
                sessionStorage.setItem(SESSION_KEY, JSON.stringify({
                    unitId: Number(unitId),
                    page: this.currentPage,
                    limit: this.limit,
                    filters: this.normalizedFilters(),
                    sortBy: this.tableSortBy,
                    sortDesc: this.tableSortDesc,
                    savedAt: Date.now()
                }));
            } catch (e) { /* ignore */ }
        },
        readSession(unitId) {
            try {
                const raw = sessionStorage.getItem(SESSION_KEY);
                if (!raw) {
                    return null;
                }
                const data = JSON.parse(raw);
                if (
                    Number(data.unitId) === Number(unitId) &&
                    Date.now() - Number(data.savedAt || 0) < SESSION_TTL_MS
                ) {
                    return data;
                }
            } catch (e) { /* ignore */ }
            return null;
        },
        clearSession() {
            try {
                sessionStorage.removeItem(SESSION_KEY);
            } catch (e) { /* ignore */ }
        },
        clearHighlightCleanupTimer() {
            if (this.highlightCleanupTimer) {
                clearTimeout(this.highlightCleanupTimer);
                this.highlightCleanupTimer = null;
            }
        },
        scheduleHighlightCleanup(unitId) {
            this.clearHighlightCleanupTimer();
            this.highlightCleanupTimer = setTimeout(() => {
                if (this._isDestroyed) {
                    return;
                }
                this.highlightedUnitId = null;
                const currentHighlight = this.$route.query && this.$route.query.highlight;
                if (!currentHighlight || String(currentHighlight) !== String(unitId)) {
                    return;
                }
                const nextQuery = Object.assign({}, this.$route.query);
                delete nextQuery.highlight;
                this.$router.replace({
                    path: this.$route.path,
                    query: nextQuery
                }).catch(() => {});
            }, HIGHLIGHT_CLEANUP_DELAY_MS);
        },
        tableRowClass(item) {
            return this.isHighlightedUnit(item) ? `mechanic-assets-highlight-target` : ``;
        },
        isHighlightedUnit(unit) {
            return Boolean(unit && this.highlightedUnitId && Number(unit.id) === Number(this.highlightedUnitId));
        },
        hasActiveHighlight() {
            return Boolean((this.$route.query && this.$route.query.highlight) || this.highlightedUnitId);
        },
        isElementVisible(element) {
            if (!element) {
                return false;
            }
            const rect = element.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        },
        getTableScrollBody() {
            return this.$el ? this.$el.querySelector(`.mechanic-assets-table .b-table-sticky-header`) : null;
        },
        captureListScrollPosition() {
            const tableScrollBody = this.getTableScrollBody();
            const windowY = typeof window !== `undefined`
                ? (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0)
                : 0;

            return {
                tableScrollTop: tableScrollBody ? tableScrollBody.scrollTop : null,
                windowY
            };
        },
        async restoreListScrollPosition(position) {
            if (!position) {
                return;
            }
            await this.$nextTick();
            await this.$nextTick();

            const tableScrollBody = this.getTableScrollBody();
            if (tableScrollBody && position.tableScrollTop !== null && position.tableScrollTop !== undefined) {
                tableScrollBody.scrollTop = position.tableScrollTop;
            }
            if (typeof window !== `undefined` && Number.isFinite(Number(position.windowY))) {
                window.scrollTo(0, Number(position.windowY));
            }
        },
        resolveHighlightTarget(unitId) {
            if (!this.$el) {
                return null;
            }
            const id = Number(unitId);
            if (!Number.isInteger(id) || id <= 0) {
                return null;
            }

            const tableMarker = this.$el.querySelector(`.mechanic-assets-table [data-mechanic-asset-id="${id}"]`);
            const tableRow = tableMarker ? (tableMarker.closest(`tr`) || tableMarker) : null;

            const mobileCard = this.$el.querySelector(`.mechanic-assets-mobile-card[data-mechanic-asset-id="${id}"]`);
            if (this.isElementVisible(tableRow)) {
                return tableRow;
            }
            if (this.isElementVisible(mobileCard)) {
                return mobileCard;
            }
            return null;
        },
        scrollElementInside(target, scrollBody) {
            if (!target || !scrollBody) {
                return;
            }
            const bodyRect = scrollBody.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const nextTop = scrollBody.scrollTop
                + targetRect.top
                - bodyRect.top
                - ((scrollBody.clientHeight - targetRect.height) / 2);

            const top = Math.max(0, nextTop);
            if (typeof scrollBody.scrollTo === `function`) {
                scrollBody.scrollTo({
                    top,
                    behavior: `smooth`
                });
            } else {
                scrollBody.scrollTop = top;
            }
        },
        applyHighlightBlink(target) {
            if (!target) {
                return;
            }
            const cls = `mechanic-assets-highlight-blink`;
            const tagName = String(target.tagName || ``).toUpperCase();
            const elements = tagName === `TR`
                ? Array.prototype.slice.call(target.children || [])
                : [target];

            target.classList.remove(cls);
            elements.forEach(element => element.classList.remove(cls));
            void target.offsetWidth;
            target.classList.add(cls);
            elements.forEach(element => element.classList.add(cls));
            const setInlineHighlight = active => elements.forEach(element => {
                element.style.transition = `background-color 0.12s ease, box-shadow 0.12s ease`;
                if (!active) {
                    element.style.backgroundColor = ``;
                    element.style.boxShadow = ``;
                } else if (tagName === `TR`) {
                    element.style.backgroundColor = `#fff3cd`;
                    element.style.boxShadow = `inset 0 0 0 2px rgba(255, 193, 7, 0.82)`;
                } else {
                    element.style.boxShadow = `0 0 0 3px rgba(255, 193, 7, 0.9), 0 4px 14px rgba(255, 193, 7, 0.28)`;
                }
            });
            let inlineHighlightActive = true;
            setInlineHighlight(true);
            const inlineBlinkInterval = setInterval(() => {
                inlineHighlightActive = !inlineHighlightActive;
                setInlineHighlight(inlineHighlightActive);
            }, 180);
            setTimeout(() => {
                clearInterval(inlineBlinkInterval);
                target.classList.remove(cls);
                elements.forEach(element => {
                    element.classList.remove(cls);
                    element.style.transition = ``;
                    element.style.backgroundColor = ``;
                    element.style.boxShadow = ``;
                });
            }, HIGHLIGHT_BLINK_DURATION_MS);
        },
        async scrollToHighlight(unitId) {
            this.highlightedUnitId = Number(unitId);
            await this.$nextTick();
            await this.$nextTick();

            const target = this.resolveHighlightTarget(unitId);
            if (!target) {
                return;
            }

            const scrollBody = target.closest(`.b-table-sticky-header`);
            if (scrollBody && scrollBody.scrollHeight > scrollBody.clientHeight) {
                this.scrollElementInside(target, scrollBody);
            } else {
                target.scrollIntoView({behavior: `smooth`, block: `center`});
            }

            setTimeout(() => {
                if (this._isDestroyed) {
                    return;
                }
                const currentTarget = this.resolveHighlightTarget(unitId) || target;
                const currentScrollBody = currentTarget.closest(`.b-table-sticky-header`);
                if (currentScrollBody && currentScrollBody.scrollHeight > currentScrollBody.clientHeight) {
                    this.scrollElementInside(currentTarget, currentScrollBody);
                }
                this.applyHighlightBlink(currentTarget);
            }, 550);
            setTimeout(() => {
                if (!this._isDestroyed && this.hasActiveHighlight()) {
                    const currentTarget = this.resolveHighlightTarget(unitId);
                    const currentScrollBody = currentTarget && currentTarget.closest(`.b-table-sticky-header`);
                    if (currentTarget && currentScrollBody && currentScrollBody.scrollHeight > currentScrollBody.clientHeight) {
                        this.scrollElementInside(currentTarget, currentScrollBody);
                    }
                }
            }, 1100);
        },
        async restoreFromHighlight(highlightId) {
            const unitId = Number(highlightId);
            if (!Number.isInteger(unitId) || unitId <= 0) {
                await this.$store.dispatch(`mechanicAssets/fetchUnits`);
                return;
            }

            const session = this.readSession(unitId);
            if (session) {
                this.clearSession();
                this.applyListContext(session);
            }

            await this.$store.dispatch(`mechanicAssets/fetchUnits`);
            await this.scrollToHighlight(unitId);
            this.scheduleHighlightCleanup(unitId);
        },
        updateMobileLayout() {
            this.isMobileLayout = window.innerWidth < 768;
        },
        async fetchInitialData() {
            this.errorMessage = null;
            this.initialDataLoading = true;
            try {
                await Promise.all([
                    this.$store.dispatch(`mechanicAssets/fetchAccessSettings`),
                    this.$store.dispatch(`mechanicAssets/fetchDictionaries`),
                    this.$store.dispatch(`getObjectsInProcess`),
                    this.$store.dispatch(`getUsers`, {full: true}),
                    this.$store.dispatch(`getDepartments`)
                ]);
                const highlight = this.$route.query && this.$route.query.highlight;
                if (highlight) {
                    await this.restoreFromHighlight(highlight);
                } else {
                    await this.$store.dispatch(`mechanicAssets/fetchUnits`);
                }
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка загрузки справочника механиков`);
            } finally {
                this.initialDataLoading = false;
            }
        },
        async autoRefreshOnResume() {
            if ((typeof document !== `undefined` && document.hidden) ||
                this.autoRefreshInProgress ||
                this.initialDataLoading ||
                this.hasActiveHighlight() ||
                this.loading) {
                return;
            }

            this.autoRefreshInProgress = true;
            const scrollPosition = this.captureListScrollPosition();
            try {
                await this.$store.dispatch(`mechanicAssets/fetchUnits`, {
                    silent: true,
                    filterOptions: false
                });
                await this.restoreListScrollPosition(scrollPosition);
            } catch (err) {
                console.error(`Не удалось автообновить справочник механиков:`, err);
            } finally {
                this.autoRefreshInProgress = false;
            }
        },
        handleVisibilityChange() {
            if (typeof document === `undefined` || !document.hidden) {
                this.autoRefreshOnResume();
            }
        },
        handlePageShow() {
            const highlight = this.$route.query && this.$route.query.highlight;
            if (highlight) {
                this.scrollToHighlight(highlight);
            }
        },
        setStatusFilter(value) {
            this.filters.status = value || null;
            this.applyFilters();
        },
        statusChipActive(value) {
            return value === null
                ? !this.filters.status
                : String(this.filters.status || ``) === String(value);
        },
        scrollStatusPanelOnWheel(event) {
            const target = event && event.currentTarget;
            if (!target || event.ctrlKey || target.scrollWidth <= target.clientWidth) {
                return;
            }
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
                ? event.deltaX
                : event.deltaY;
            if (!delta) {
                return;
            }
            const previousScrollLeft = target.scrollLeft;
            target.scrollLeft += delta;
            if (target.scrollLeft !== previousScrollLeft) event.preventDefault();
        },
        filterObjectLocations(options, search) {
            const query = String(search || ``).trim().toLocaleLowerCase(`ru`);
            if (!query) return options;
            const matches = option => option.text.toLocaleLowerCase(`ru`).includes(query);
            const matchingGroups = new Set(options.filter(option => option.type === `group` && matches(option))
                .map(option => String(option.groupId)));
            const parentGroups = new Set(options.filter(option => option.type !== `group` && matches(option))
                .map(option => String(option.groupId)));
            return options.filter(option => matches(option) || matchingGroups.has(String(option.groupId))
                || (option.type === `group` && parentGroups.has(String(option.groupId))));
        },
        filterActualOptions(options, valueMap, selectedValue) {
            const selectedKey = selectedValue !== null && selectedValue !== undefined && selectedValue !== ``
                ? String(selectedValue)
                : null;

            return (options || []).filter(option => {
                if (!option || option.value === null || option.value === undefined || option.value === ``) {
                    return true;
                }
                const key = String(option.value);
                return Boolean(valueMap && valueMap[key]) || key === selectedKey;
            });
        },
        applyFilters() {
            this.currentPage = 1;
            this.$store.dispatch(`mechanicAssets/applyFilters`, this.normalizedFilters())
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка применения фильтра`);
                });
        },
        onArchiveChange(value) {
            this.filters.archive = Boolean(value);
            this.applyFilters();
        },
        clearFilterItem(key) {
            if (key === `q`) {
                this.filters.q = ``;
            } else if (key === `archive`) {
                this.filters.archive = false;
            } else if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
                this.filters[key] = null;
            }
            this.applyFilters();
        },
        resetFilters() {
            this.filters = defaultFilters();
            this.currentPage = 1;
            this.$store.dispatch(`mechanicAssets/applyFilters`, this.normalizedFilters())
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка сброса фильтров`);
                });
        },
        onSearchInput() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }
            this.searchTimer = setTimeout(() => this.applyFilters(), 300);
        },
        onPageChange(page) {
            this.$store.dispatch(`mechanicAssets/setPage`, page)
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка загрузки страницы`);
                });
        },
        onLimitChange(limit) {
            this.currentPage = 1;
            this.$store.dispatch(`mechanicAssets/setLimit`, Number(limit))
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка изменения размера страницы`);
                });
        },
        onSortChanged(ctx) {
            const sortBy = ctx && ctx.sortBy ? ctx.sortBy : `id`;
            const sortDesc = ctx ? Boolean(ctx.sortDesc) : true;
            this.tableSortBy = sortBy;
            this.tableSortDesc = sortDesc;
            this.currentPage = 1;
            this.$store.dispatch(`mechanicAssets/setSort`, {
                sortBy,
                sortDesc
            }).catch(err => {
                this.errorMessage = this.errorText(err, `Ошибка сортировки справочника`);
            });
        },
        onMobileSortChange() {
            this.onSortChanged({
                sortBy: this.tableSortBy,
                sortDesc: this.tableSortDesc
            });
        },
        toggleMobileSortDirection() {
            this.tableSortDesc = !this.tableSortDesc;
            this.onMobileSortChange();
        },
        normalizedFilters() {
            return Object.assign({}, this.filters, {
                q: this.filters.q || null,
                status: this.filters.status || null,
                catalog_id: this.filters.catalog_id || null,
                warehouse_id: this.filters.warehouse_id || null,
                building_process_id: this.filters.building_process_id || null,
                object_group_id: this.filters.object_group_id || null,
                object_group_scope_id: this.filters.object_group_scope_id || null,
                responsible_user_id: this.filters.responsible_user_id || null
            });
        },
        addFilterItem(result, key, label, options, value, variant) {
            if (value === null || value === undefined || value === ``) {
                return;
            }
            const found = (options || []).find(option => String(option.value) === String(value));
            result.push({
                key,
                label,
                text: `${label}: ${found ? found.text : value}`,
                variant: variant || (found && found.variant) || `secondary`
            });
        },
        async prepareHighlightNavigation(unitId) {
            this.saveReturnSession(unitId);
            if (this.$route.path !== `/mechanic-assets`) {
                return;
            }
            const currentHighlight = this.$route.query && this.$route.query.highlight;
            if (String(currentHighlight || ``) === String(unitId)) {
                return;
            }
            const nextQuery = Object.assign({}, this.$route.query, {
                highlight: String(unitId)
            });
            await this.$router.replace({
                path: this.$route.path,
                query: nextQuery
            }).catch(() => {});
        },
        async openUnit(id) {
            const unitId = Number(id);
            if (!Number.isInteger(unitId) || unitId <= 0) {
                return Promise.resolve();
            }
            await this.prepareHighlightNavigation(unitId);
            return this.$router.push({
                name: `MechanicAssetCard`,
                params: {id: String(unitId)}
            }).catch(err => {
                if (!err || err.name !== `NavigationDuplicated`) {
                    throw err;
                }
            });
        },
        openUnitFromRow(item) {
            if (item && item.id) {
                this.openUnit(item.id);
            }
        },
        openCreateModal() {
            this.createModalMode = `create`;
            this.createAccountingType = `inventory`;
            this.createForm = emptyCreateForm();
            this.resetInlineCatalogForm();
            this.showCreateModal = true;
        },
        openCopyModal(unit) {
            if (!unit || !unit.id || !this.canCreate) {
                return;
            }
            this.createModalMode = `copy`;
            this.resetInlineCatalogForm();
            this.createAccountingType = this.isQuantitativeUnit(unit) ? `quantity` : `inventory`;
            this.createForm = this.createCopyForm(unit);
            this.showCreateModal = true;
        },
        async submitCreateForm() {
            if (!this.isCreateFormValid) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                const payload = Object.assign({}, this.createForm, {
                    accounting_type: this.createAccountingType === `quantity` ? `quantity` : `inventory`
                });
                const unit = await this.$store.dispatch(`mechanicAssets/createUnit`, this.cleanPayload(payload));
                notifyInstallPromotionSuccess({source: 'mechanic-asset-created'});
                this.showCreateModal = false;
                if (unit && unit.id) {
                    await this.openUnit(unit.id);
                }
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка создания позиции`);
            } finally {
                this.isSubmitting = false;
            }
        },
        resetCreateForm() {
            this.createModalMode = `create`;
            this.createAccountingType = `inventory`;
            this.createForm = emptyCreateForm();
            this.resetInlineCatalogForm();
            this.isSubmitting = false;
        },
        openInlineCatalogForm() {
            if (!this.showInlineCatalogForm) {
                this.inlineCatalogForm = emptyCatalogForm();
            }
            this.showInlineCatalogForm = true;
        },
        resetInlineCatalogForm() {
            this.inlineCatalogForm = emptyCatalogForm();
            this.showInlineCatalogForm = false;
            this.inlineCatalogSubmitting = false;
        },
        async submitInlineCatalogForm() {
            if (this.inlineCatalogSubmitting || !this.inlineCatalogForm.name) {
                return;
            }
            this.inlineCatalogSubmitting = true;
            this.errorMessage = null;
            try {
                const payload = this.cleanPayload(Object.assign({}, this.inlineCatalogForm, {
                    id: null,
                    category: this.normalizeCatalogCategory(this.inlineCatalogForm.category) || `other`,
                    is_active: true
                }));
                const row = await this.$store.dispatch(`mechanicAssets/createCatalog`, payload);
                if (row && row.id) {
                    this.createForm.catalog_id = row.id;
                }
                this.showInlineCatalogForm = false;
                this.inlineCatalogForm = emptyCatalogForm();
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка создания группы техники`);
            } finally {
                this.inlineCatalogSubmitting = false;
            }
        },
        createCatalogCategoryOption(searchText) {
            const value = this.normalizeCatalogCategory(searchText);
            return {
                value,
                text: value
            };
        },
        normalizeCatalogCategory(value) {
            if (value && typeof value === `object`) {
                return this.normalizeCatalogCategory(value.value || value.text || value.label);
            }
            return String(value || ``).trim().replace(/\s+/g, ` `);
        },
        normalizeCreateAccountingType() {
            const accountingType = this.createAccountingType === `quantity` ? `quantity` : `inventory`;
            if (this.createAccountingType !== accountingType) {
                this.createAccountingType = accountingType;
                return;
            }
            this.createForm.accounting_type = accountingType;
            this.applyCreateAccountingDefaults();
        },
        normalizeCreateLocation() {
            if (this.createForm.location_type !== `object_group`) this.createForm.object_group_id = null;
            if (this.createUsesWarehouse) {
                this.createForm.building_process_id = null;
                return;
            }
            if (this.createUsesObject) {
                this.createForm.warehouse_id = null;
                return;
            }
            this.createForm.warehouse_id = null;
            this.createForm.building_process_id = null;
        },
        applyCreateCatalogDefaults() {
            this.applyCreateAccountingDefaults();
        },
        applyCreateAccountingDefaults() {
            if (this.createUsesQuantityAccounting) {
                if (!this.createForm.unit) {
                    this.createForm.unit = `шт`;
                }
                this.createForm.current_meter_type = null;
                this.createForm.current_meter_value = null;
                this.createForm.inventory_number = null;
                this.createForm.serial_number = null;
                this.createForm.plate_number = null;
                if (this.createForm.quantity === null || this.createForm.quantity === undefined) {
                    this.createForm.quantity = 0;
                }
                if (!this.createForm.location_type || this.createForm.location_type === `unknown`) {
                    this.createForm.location_type = `warehouse`;
                }
            } else {
                this.createForm.quantity = 1;
            }
        },
        createCopyForm(unit) {
            const form = emptyCreateForm();
            const isQuantity = this.isQuantitativeUnit(unit);
            return Object.assign(form, {
                catalog_id: unit.catalog_id || null,
                display_name: unit.display_name || null,
                status: unit.status === `archive` ? `stock` : (unit.status || `stock`),
                location_type: unit.location_type || `warehouse`,
                warehouse_id: unit.warehouse_id || null,
                building_process_id: unit.building_process_id || null,
                object_group_id: unit.object_group_id || null,
                responsible_user_id: unit.responsible_user_id || null,
                quantity: isQuantity ? (unit.quantity || 0) : 1,
                accounting_type: isQuantity ? `quantity` : `inventory`,
                unit: unit.unit || null,
                inventory_number: null,
                serial_number: null,
                plate_number: null,
                current_meter_type: isQuantity ? null : (unit.current_meter_type || `moto_hour`),
                current_meter_value: isQuantity ? null : (unit.current_meter_value === undefined ? null : unit.current_meter_value),
                technical_condition: unit.technical_condition || null,
                accounting_company_name: unit.accounting_company_name || unit.owner_name || null,
                owner_name: unit.owner_name || unit.accounting_company_name || null,
                owner_kind: unit.owner_kind || `own`,
                comment: unit.comment || null,
                history_comment: `Создано копированием позиции #${unit.id}`
            });
        },
        cleanPayload(payload) {
            const result = {};
            Object.keys(payload).forEach(key => {
                result[key] = payload[key] === `` ? null : payload[key];
            });
            if (Object.prototype.hasOwnProperty.call(result, `accounting_company_name`)) {
                result.owner_name = result.accounting_company_name;
            }
            return result;
        },
        openWarehousesModal() {
            this.resetWarehouseForm();
            this.showWarehousesModal = true;
        },
        openCatalogModal() {
            this.resetCatalogForm();
            this.showCatalogModal = true;
        },
        openAccessModal() {
            const config = this.accessSettings.access_config || createDefaultAccessConfig();
            this.settingsForm = Object.assign(createDefaultAccessConfig(), cloneJson(config));
            if (!this.settingsForm.read) {
                this.$set(this.settingsForm, `read`, {participants: []});
            }
            if (!this.settingsForm.write) {
                this.$set(this.settingsForm, `write`, {participants: []});
            }
            this.$set(
                this.settingsForm,
                `read_history_for_readers`,
                this.settingsForm.read_history_for_readers === true
            );
            this.showAccessModal = true;
        },
        resetAccessModal() {
            this.settingsForm = createDefaultAccessConfig();
            this.isSettingsSubmitting = false;
        },
        async saveAccessSettings() {
            this.isSettingsSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/updateAccessSettings`, cloneJson(this.settingsForm));
                await this.$store.dispatch(`mechanicAssets/fetchUnits`);
                this.showAccessModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка сохранения настройки доступа`);
            } finally {
                this.isSettingsSubmitting = false;
            }
        },
        resetWarehouseForm() {
            this.warehouseForm = emptyWarehouseForm();
        },
        resetCatalogForm() {
            this.catalogForm = emptyCatalogForm();
        },
        editWarehouse(row) {
            this.warehouseForm = {
                id: row.id,
                name: row.name,
                warehouse_type: row.warehouse_type || `warehouse`,
                is_active: row.is_active !== false,
                comment: row.comment || null
            };
        },
        editCatalog(row) {
            this.catalogForm = {
                id: row.id,
                name: row.name,
                category: row.category || `other`,
                brand: row.brand || null,
                model: row.model || null,
                is_active: row.is_active !== false,
                comment: row.comment || null
            };
        },
        async submitWarehouseForm() {
            if (this.isSubmitting || !this.warehouseForm.name) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                const payload = this.cleanPayload(this.warehouseForm);
                if (this.warehouseForm.id) {
                    await this.$store.dispatch(`mechanicAssets/updateWarehouse`, {
                        id: this.warehouseForm.id,
                        payload
                    });
                } else {
                    await this.$store.dispatch(`mechanicAssets/createWarehouse`, payload);
                }
                this.resetWarehouseForm();
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка сохранения склада`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async submitCatalogForm() {
            if (this.isSubmitting || !this.catalogForm.name) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                const payload = this.cleanPayload(Object.assign({}, this.catalogForm, {
                    category: this.normalizeCatalogCategory(this.catalogForm.category) || `other`
                }));
                if (this.catalogForm.id) {
                    await this.$store.dispatch(`mechanicAssets/updateCatalog`, {
                        id: this.catalogForm.id,
                        payload
                    });
                } else {
                    await this.$store.dispatch(`mechanicAssets/createCatalog`, payload);
                }
                this.resetCatalogForm();
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка сохранения группы техники`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async archiveWarehouse(row) {
            if (!row || !row.id || this.isSubmitting) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/archiveWarehouse`, {id: row.id, payload: {}});
                if (this.warehouseForm.id === row.id) {
                    this.resetWarehouseForm();
                }
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка архивирования склада`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async archiveCatalog(row) {
            if (!row || !row.id || this.isSubmitting) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/archiveCatalog`, {id: row.id, payload: {}});
                if (this.catalogForm.id === row.id) {
                    this.resetCatalogForm();
                }
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка архивирования группы техники`);
            } finally {
                this.isSubmitting = false;
            }
        },
        unitTitle(item) {
            return item.display_name || item.catalog_name || (item.Catalog && item.Catalog.name) || `Позиция #${item.id}`;
        },
        unitSubtitle(item) {
            const parts = [
                item.inventory_number ? `Инв. ${item.inventory_number}` : null,
                item.qr_code ? `QR ${item.qr_code}` : null,
                item.plate_number ? `Гос. ${item.plate_number}` : null
            ];
            return parts.filter(Boolean).join(` · `);
        },
        locationName(item) {
            if (item.location_type === `object_group`) return `Группа: ${item.object_group_name || `#${item.object_group_id}`} — без конкретного объекта`;
            return item.warehouse_name || item.object_name || item.source_warehouse_name || item.source_project_name || ``;
        },
        previewFileUrl(unit, file, width, height) {
            if (!unit || !unit.id || !file || !file.id) {
                return `#`;
            }
            const token = this.$store && this.$store.state ? this.$store.state.token : null;
            const params = [
                `thumb=1`,
                `w=${Number(width) || 96}`,
                `h=${Number(height) || 72}`
            ];
            if (token) {
                params.push(`gjuid=${encodeURIComponent(token)}`);
            }
            return `/api/mechanic-assets/units/${unit.id}/files/${file.id}?${params.join(`&`)}`;
        },
        activeTransfer(item) {
            if (!item) {
                return null;
            }
            if (item.status !== `in_transfer`) {
                return null;
            }
            const transfer = item.active_transfer || item.transfer_state || null;
            return transfer && transfer.active === true ? transfer : null;
        },
        transferPointText(point) {
            if (!point) {
                return `не указано`;
            }
            return point.object_name ||
                point.warehouse_name ||
                point.renter_name ||
                point.location_label ||
                point.location_type ||
                `не указано`;
        },
        transferText(item) {
            const transfer = this.activeTransfer(item);
            if (!transfer) {
                return ``;
            }
            return `${this.transferPointText(transfer.from)} -> ${this.transferPointText(transfer.to)}`;
        },
        transferFromText(item) {
            const transfer = this.activeTransfer(item);
            return transfer ? this.transferPointText(transfer.from) : `не указано`;
        },
        transferToText(item) {
            const transfer = this.activeTransfer(item);
            return transfer ? this.transferPointText(transfer.to) : `не указано`;
        },
        transferRequestId(item) {
            const transfer = this.activeTransfer(item);
            return transfer && transfer.request_id ? transfer.request_id : null;
        },
        transferRequestNumber(item) {
            const transfer = this.activeTransfer(item);
            return transfer ? (transfer.request_number || transfer.request_id) : ``;
        },
        transferRequestTo(item) {
            const requestId = this.transferRequestId(item);
            return requestId ? `/requests/${requestId}` : `/requests`;
        },
        catalogOptionText(row) {
            if (!row) {
                return ``;
            }
            const details = [row.brand, row.model].filter(Boolean).join(` `);
            return details && details !== row.name
                ? `${row.name} / ${details}`
                : row.name;
        },
        isQuantitativeUnit(item) {
            return item && item.accounting_type === `quantity`;
        },
        mobileMeterOrQuantityText(item) {
            if (this.isQuantitativeUnit(item)) {
                return this.quantityText(item);
            }
            if (item && item.current_meter_value !== null && item.current_meter_value !== undefined) {
                return `${item.current_meter_value} ${this.meterTypeLabel(item.current_meter_type)}`.trim();
            }
            return `-`;
        },
        quantityText(item) {
            if (!item) {
                return `-`;
            }
            const quantity = item.quantity === null || item.quantity === undefined ? `0` : item.quantity;
            return `${this.formatQuantity(quantity)} ${item.unit || `шт`}`;
        },
        formatQuantity(value) {
            const number = Number(value);
            return Number.isFinite(number)
                ? number.toLocaleString(`ru-RU`, {maximumFractionDigits: 3})
                : value;
        },
        accountingTypeText(value) {
            return ACCOUNTING_TYPE_LABELS[value] || value || `-`;
        },
        catalogCategoryText(value) {
            return CATALOG_CATEGORY_LABELS[value] || value || `-`;
        },
        warehouseTypeText(value) {
            return WAREHOUSE_TYPE_LABELS[value] || value || `-`;
        },
        meterTypeLabel(value) {
            if (value === `km`) {
                return `км`;
            }
            if (value === `moto_hour`) {
                return `м/ч`;
            }
            return value || ``;
        },
        statusVariant(status) {
            return getMechanicAssetStatusVariant(status);
        },
        userName(user) {
            return [user.last_name, user.first_name].filter(Boolean).join(` `).trim();
        },
        errorText(err, fallback) {
            if (err && err.response && err.response.data) {
                if (typeof err.response.data === `string`) {
                    return err.response.data;
                }
                if (err.response.data.msg) {
                    return err.response.data.msg;
                }
            }
            return fallback;
        }
    }
};
</script>

<style scoped>
.mechanic-assets-page {
    min-width: 0;
    overflow-x: hidden;
}

.mechanic-assets-toolbar {
    display: grid;
    grid-template-columns: minmax(220px, 1.5fr) repeat(4, minmax(150px, 1fr)) auto;
    gap: 8px;
    align-items: center;
    margin: 8px 0;
}

.mechanic-assets-select {
    min-width: 0;
}

.mechanic-assets-select ::v-deep(.vs__dropdown-toggle) {
    min-height: 31px;
    border-color: #ced4da;
}

.mechanic-assets-select ::v-deep(.vs__search),
.mechanic-assets-select ::v-deep(.vs__selected) {
    margin-top: 2px;
}

.mechanic-assets-archive {
    min-width: 88px;
    margin: 0;
}

.mechanic-assets-status-panel {
    display: flex;
    gap: 6px;
    margin: 4px 0 8px;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;
}

.mechanic-assets-status-panel::-webkit-scrollbar {
    display: none;
}

.mechanic-assets-status-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
    min-height: 31px;
    padding: 4px 10px;
    border: 1px solid transparent;
    border-radius: 999px;
    color: #495057;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    transition: box-shadow .15s ease, transform .15s ease, filter .15s ease;
}

.mechanic-assets-status-chip:hover {
    filter: brightness(.96);
}

.mechanic-assets-status-chip--active {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, .18);
    transform: translateY(-1px);
    color: #fff;
}

.mechanic-assets-status-chip__check {
    font-size: 11px;
}

.mechanic-assets-status-chip--secondary {
    background: #f1f3f5;
    border-color: #adb5bd;
    color: #495057;
}

.mechanic-assets-status-chip--danger {
    background: #fbe9eb;
    border-color: #f1aeb5;
    color: #842029;
}

.mechanic-assets-status-chip--warning {
    background: #fff3cd;
    border-color: #ffda6a;
    color: #664d03;
}

.mechanic-assets-status-chip--primary {
    background: #e7f1ff;
    border-color: #9ec5fe;
    color: #084298;
}

.mechanic-assets-status-chip--info {
    background: #cff4fc;
    border-color: #9eeaf9;
    color: #055160;
}

.mechanic-assets-status-chip--success {
    background: #d1e7dd;
    border-color: #a3cfbb;
    color: #0f5132;
}

.mechanic-assets-status-chip--dark {
    background: #e9ecef;
    border-color: #adb5bd;
    color: #212529;
}

.mechanic-assets-status-chip--light {
    background: #f8f9fa;
    border-color: #dee2e6;
    color: #212529;
}

.mechanic-assets-status-chip--secondary.mechanic-assets-status-chip--active {
    background: #6c757d;
    border-color: #6c757d;
}

.mechanic-assets-status-chip--danger.mechanic-assets-status-chip--active {
    background: #dc3545;
    border-color: #dc3545;
}

.mechanic-assets-status-chip--warning.mechanic-assets-status-chip--active {
    background: #ffc107;
    border-color: #ffc107;
    color: #212529;
}

.mechanic-assets-status-chip--primary.mechanic-assets-status-chip--active {
    background: #007bff;
    border-color: #007bff;
}

.mechanic-assets-status-chip--info.mechanic-assets-status-chip--active {
    background: #17a2b8;
    border-color: #17a2b8;
}

.mechanic-assets-status-chip--success.mechanic-assets-status-chip--active {
    background: #28a745;
    border-color: #28a745;
}

.mechanic-assets-status-chip--dark.mechanic-assets-status-chip--active {
    background: #343a40;
    border-color: #343a40;
}

.mechanic-assets-status-chip--light.mechanic-assets-status-chip--active {
    background: #f8f9fa;
    border-color: #d6d8db;
    color: #212529;
}

.mechanic-assets-active-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    margin: 4px 0 8px;
    padding: 6px 8px;
    border: 1px solid #ffe08a;
    border-radius: 5px;
    background: #fff8e1;
    color: #5c4300;
    font-size: 13px;
}

.mechanic-assets-active-filters__label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    font-weight: 700;
}

.mechanic-assets-active-filter {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    min-height: 28px;
    padding: 3px 8px;
    border: 1px solid transparent;
    border-radius: 999px;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.mechanic-assets-active-filter__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mechanic-assets-active-filter__remove {
    margin-left: 2px;
    opacity: .85;
}

.mechanic-assets-active-filter--primary {
    background: #007bff;
    border-color: #007bff;
}

.mechanic-assets-active-filter--secondary {
    background: #6c757d;
    border-color: #6c757d;
}

.mechanic-assets-active-filter--danger {
    background: #dc3545;
    border-color: #dc3545;
}

.mechanic-assets-active-filter--warning {
    background: #ffc107;
    border-color: #ffc107;
    color: #212529;
}

.mechanic-assets-active-filter--info {
    background: #17a2b8;
    border-color: #17a2b8;
}

.mechanic-assets-active-filter--success {
    background: #28a745;
    border-color: #28a745;
}

.mechanic-assets-active-filter--dark {
    background: #343a40;
    border-color: #343a40;
}

.mechanic-assets-active-filter--light {
    background: #f8f9fa;
    border-color: #d6d8db;
    color: #212529;
}

.mechanic-assets-active-filters__reset {
    flex: 0 0 auto;
    min-height: 28px;
    padding: 3px 8px;
    border: 1px solid #b38300;
    border-radius: 4px;
    background: rgba(255, 255, 255, .72);
    color: #5c4300;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
}

.mechanic-assets-transfer-card {
    margin-top: 5px;
    padding: 6px 8px;
    border-left: 4px solid #17a2b8;
    border-radius: 4px;
    background: #dff5fb;
    color: #0c5460;
}

.mechanic-assets-transfer-card--mobile {
    margin-top: 6px;
}

.mechanic-assets-transfer-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    margin-bottom: 4px;
}

.mechanic-assets-transfer-badge {
    text-transform: uppercase;
    letter-spacing: 0;
}

.mechanic-assets-transfer-request {
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.2;
}

.mechanic-assets-transfer-route {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.mechanic-assets-transfer-point {
    display: grid;
    grid-template-columns: 52px minmax(0, 1fr);
    align-items: flex-start;
    column-gap: 6px;
    min-width: 0;
    padding: 4px 6px;
    border: 1px solid rgba(23, 162, 184, 0.35);
    border-radius: 4px;
    background: #fff;
}

.mechanic-assets-transfer-point span {
    display: block;
    color: #6c757d;
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1.25;
    text-transform: uppercase;
}

.mechanic-assets-transfer-point strong {
    display: block;
    min-width: 0;
    overflow-wrap: anywhere;
    white-space: normal;
    color: #083b44;
    font-size: 0.78rem;
    line-height: 1.25;
}

.mechanic-assets-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin: 8px 0;
    flex-wrap: wrap;
}

.mechanic-assets-view-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
}

.mechanic-assets-pagination {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mechanic-assets-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mechanic-assets-bottom-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 12px 0 4px;
    padding-top: 10px;
    border-top: 1px solid #e9ecef;
}

.mechanic-assets-bottom-pagination__summary {
    white-space: nowrap;
}

.mechanic-assets-page-size {
    width: 86px;
}

.mechanic-assets-name {
    font-weight: 600;
    line-height: 1.2;
}

.mechanic-assets-photo-col,
.mechanic-assets-table ::v-deep(.mechanic-assets-photo-col) {
    width: 54px;
    min-width: 54px;
}

.mechanic-assets-photo-thumb {
    width: 44px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 0;
    border: 1px solid #d7dde4;
    border-radius: 4px;
    background: #f8f9fa;
    color: #8a939d;
    vertical-align: middle;
}

button.mechanic-assets-photo-thumb {
    cursor: pointer;
}

button.mechanic-assets-photo-thumb:hover {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.14);
}

.mechanic-assets-photo-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.mechanic-assets-photo-thumb--empty i {
    font-size: 14px;
}

.mechanic-assets-table ::v-deep(tbody tr) {
    cursor: pointer;
}

.mechanic-assets-table ::v-deep(tbody tr:hover > td) {
    background-color: #eef4ff;
}

@keyframes mechanic-assets-highlight-cell-blink-anim {
    0%, 100% {
        background-color: inherit;
        box-shadow: inset 0 0 0 0 rgba(255, 193, 7, 0);
    }
    35%, 70% {
        background-color: #fff3cd;
        box-shadow: inset 0 0 0 2px rgba(255, 193, 7, 0.72);
    }
}

@keyframes mechanic-assets-highlight-card-blink-anim {
    0%, 100% {
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.08);
    }
    35%, 70% {
        box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.86), 0 4px 14px rgba(255, 193, 7, 0.28);
    }
}

.mechanic-assets-table ::v-deep(tbody tr.mechanic-assets-highlight-blink > td),
.mechanic-assets-table ::v-deep(tbody td.mechanic-assets-highlight-blink) {
    animation: mechanic-assets-highlight-cell-blink-anim 0.32s ease-in-out 5;
}

.mechanic-assets-mobile-card.mechanic-assets-highlight-blink {
    animation: mechanic-assets-highlight-card-blink-anim 0.32s ease-in-out 5;
}

.mechanic-assets-mobile-sort {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    margin: 8px 0;
    max-width: 420px;
}

.mechanic-assets-mobile-list {
    display: none;
}

.mechanic-assets-mobile-card {
    position: relative;
    overflow: hidden;
}

.mechanic-assets-mobile-card__accent {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    z-index: 1;
}

.mechanic-assets-mobile-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 46px 7px 14px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;
}

.mechanic-assets-mobile-card__photo {
    flex: 0 0 52px;
    width: 52px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 0;
    border: 1px solid #d7dde4;
    border-radius: 4px;
    background: #fff;
    color: #8a939d;
}

button.mechanic-assets-mobile-card__photo {
    cursor: pointer;
}

.mechanic-assets-mobile-card__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.mechanic-assets-mobile-card__photo--empty i {
    font-size: 15px;
}

.mechanic-assets-mobile-card__title-block {
    flex: 1 1 auto;
    min-width: 0;
}

.mechanic-assets-mobile-card__title {
    display: block;
    overflow: hidden;
    color: #212529;
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
}

.mechanic-assets-mobile-card__copy {
    position: absolute;
    top: 7px;
    right: 8px;
    z-index: 4;
    width: 28px;
    height: 28px;
    padding: 0;
    line-height: 1;
}

.mechanic-assets-mobile-card__status {
    flex: 0 0 auto;
    max-width: 40%;
    white-space: normal;
    text-align: right;
}

.mechanic-assets-mobile-card__body {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 10px;
    padding: 9px 10px 8px 14px;
}

.mechanic-assets-mobile-card__body span {
    display: block;
    font-size: 12px;
}

.mechanic-assets-mobile-card__body strong {
    display: block;
    min-width: 0;
    overflow-wrap: anywhere;
    line-height: 1.25;
}

.mechanic-assets-mobile-card__footer {
    display: flex;
    justify-content: flex-end;
    padding: 0 10px 10px 14px;
}

.mechanic-assets-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(240px, 100%), 1fr));
    gap: 16px;
    align-items: stretch;
}

.mechanic-assets-tiles > .text-center {
    grid-column: 1 / -1;
}

.mechanic-assets-catalog-select-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    min-width: 0;
}

.mechanic-assets-inline-catalog-form {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    align-items: center;
    margin-top: 8px;
    padding: 10px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
}

.mechanic-assets-inline-catalog-form .btn {
    white-space: nowrap;
}

.mechanic-assets-management-form,
.mechanic-assets-catalog-form {
    display: grid;
    gap: 8px;
    align-items: center;
    margin-bottom: 12px;
}

.mechanic-assets-management-form {
    grid-template-columns: minmax(180px, 1fr) 150px auto auto auto;
}

.mechanic-assets-catalog-form {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

@media (max-width: 992px) {
    .mechanic-assets-toolbar {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .mechanic-assets-management-form,
    .mechanic-assets-catalog-form,
    .mechanic-assets-inline-catalog-form {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 993px) and (max-width: 1500px) {
    .mechanic-assets-toolbar {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 576px) {
    .mechanic-assets-toolbar {
        grid-template-columns: 1fr;
    }

    .mechanic-assets-status-panel {
        margin-right: -12px;
        margin-left: -12px;
        padding: 0 12px 4px;
    }

    .mechanic-assets-status-chip {
        min-height: 34px;
        padding: 5px 11px;
        font-size: 13px;
    }

    .mechanic-assets-active-filters {
        align-items: stretch;
        margin-right: -4px;
        margin-left: -4px;
    }

    .mechanic-assets-active-filters__label,
    .mechanic-assets-active-filters__reset {
        width: 100%;
    }

    .mechanic-assets-active-filter {
        max-width: 100%;
    }

    .mechanic-assets-transfer-card {
        width: 100%;
    }

    .mechanic-assets-transfer-point {
        grid-template-columns: 48px minmax(0, 1fr);
        padding: 4px 5px;
    }

    .mechanic-assets-transfer-point strong {
        font-size: 0.76rem;
    }

    .mechanic-assets-meta {
        align-items: stretch;
        flex-direction: column;
    }

    .mechanic-assets-actions {
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .mechanic-assets-actions > .btn {
        flex: 1 1 calc(50% - 8px);
    }

    .mechanic-assets-bottom-pagination {
        align-items: stretch;
        flex-direction: column;
    }

    .mechanic-assets-pagination {
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .mechanic-assets-catalog-select-row {
        flex-direction: column;
    }

    .mechanic-assets-catalog-select-row .btn {
        width: 100%;
    }

    .mechanic-assets-management-form,
    .mechanic-assets-catalog-form,
    .mechanic-assets-inline-catalog-form {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767.98px) {
    .mechanic-assets-table {
        display: none;
    }

    .mechanic-assets-mobile-sort {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 8px;
        margin: 8px 0;
    }

    .mechanic-assets-mobile-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
    }
}

@media (max-width: 420px) {
    .mechanic-assets-mobile-card__body {
        grid-template-columns: 1fr;
    }
}
</style>

<style>
/* The popover is mounted under body, outside the scoped registry styles. */
.mechanic-assets-view-hint.popover {
    max-width: min(300px, calc(100vw - 24px));
}

.mechanic-assets-view-hint__content {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.mechanic-assets-view-hint__content .close {
    flex: 0 0 auto;
    padding: 0 2px;
}
</style>
